/**
 * Asset vault. CRUD over Assets with category, institution, last4,
 * estimated value, custodian notes, and nominee binding.
 */
import { store } from '../store.ts';
import { withVault, injectCommonStyles, escapeHtml } from './_shared.ts';
import { newId, ASSET_CATEGORIES, ASSET_DISPLAY, formatINR, toPaise, totalAssetValue } from '../types.ts';
import type { Asset, AssetCategory, AssetId, NomineeId } from '../types.ts';

const TPL = /* html */ `
<style>
.summary {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
}
.summary .total {
  font-family: var(--display);
  font-size: clamp(28px, 5vw, 38px);
  color: var(--indigo);
  font-weight: 600;
  letter-spacing: -0.01em;
}
.summary .lab { font-family: var(--mono); font-size: 10px; letter-spacing: 0.28em; color: var(--ink-faint); text-transform: uppercase; }
.cat-mark {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--indigo);
  padding: 2px 6px;
  border: 1px solid var(--rule-indigo);
  border-radius: 2px;
}
.nominee-chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.nominee-chip {
  font-family: var(--mono);
  font-size: 9.5px;
  padding: 2px 6px;
  border: 1px solid var(--rule-hair);
  border-radius: 99px;
  color: var(--ink-dim);
}
</style>
<section class="frame">
  <h2>Asset vault <span class="deva">परिसंपत्तियाँ</span></h2>
  <div class="kicker">Bank · MF · demat · insurance · property · digital</div>
  <div class="summary">
    <div>
      <div class="lab">Estimated total</div>
      <div class="total" id="total">—</div>
    </div>
    <div>
      <div class="lab">Count</div>
      <div class="total" id="count" style="font-size: 18px;">0</div>
    </div>
  </div>
  <div class="list" id="list"></div>
  <form class="form-grid" id="form">
    <div class="row">
      <label class="field">Category
        <select id="cat" required>
          ${ASSET_CATEGORIES.map((c) => `<option value="${c}">${ASSET_DISPLAY[c].en} · ${ASSET_DISPLAY[c].hi}</option>`).join('')}
        </select>
      </label>
      <label class="field">Institution
        <input id="inst" type="text" required placeholder="HDFC, Zerodha, LIC" />
      </label>
    </div>
    <div class="row">
      <label class="field">Identifier last 4
        <input id="id4" type="text" maxlength="4" placeholder="****" required />
      </label>
      <label class="field">Estimated value (₹)
        <input id="val" type="number" min="0" step="100" placeholder="500000" required />
      </label>
    </div>
    <label class="field">Custodian notes (where to find proof)
      <input id="cust" type="text" placeholder="e.g. policy in bedroom locker · password in vault" />
    </label>
    <label class="field">Nominees (comma-separated names from your registry)
      <input id="noms" type="text" placeholder="Priya, Arjun" />
    </label>
    <div class="btn-row">
      <button type="submit" class="btn">Add asset</button>
      <button type="reset" class="btn ghost">Reset</button>
    </div>
  </form>
</section>
`;

export class WasiyatAssetVault extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, (v, root) => {
      const list = root.getElementById('list');
      const total = root.getElementById('total');
      const count = root.getElementById('count');
      if (total) total.textContent = formatINR(totalAssetValue(v));
      if (count) count.textContent = String(v.assets.length);
      if (!list) return;
      if (v.assets.length === 0) {
        list.innerHTML = `<div class="list-empty">No assets recorded yet.</div>`;
        return;
      }
      list.innerHTML = v.assets.map((a) => {
        const cat = ASSET_DISPLAY[a.category];
        const noms = a.nominee_ids
          .map((id) => v.nominees.find((n) => n.id === id)?.display_name)
          .filter(Boolean) as string[];
        return `<div class="list-row">
          <div>
            <div class="title">${escapeHtml(a.institution)} · <span class="cat-mark">${escapeHtml(cat.en)}</span></div>
            <div class="sub">****${escapeHtml(a.identifier_last4)}${a.custodian_notes ? ' · ' + escapeHtml(a.custodian_notes.slice(0, 80)) : ''}</div>
            ${noms.length > 0 ? `<div class="nominee-chips">${noms.map((n) => `<span class="nominee-chip">${escapeHtml(n)}</span>`).join('')}</div>` : ''}
          </div>
          <div style="display:grid;gap:6px;justify-items:end;">
            <div class="meta">${formatINR(a.estimated_value_paise)}</div>
            <button class="btn tiny ghost" data-del="${a.id}">Remove</button>
          </div>
        </div>`;
      }).join('');
      list.querySelectorAll<HTMLButtonElement>('button[data-del]').forEach((b) => {
        b.addEventListener('click', () => {
          const id = b.dataset['del'] as AssetId;
          void store.mutate((d) => { d.assets = d.assets.filter((x) => x.id !== id); });
        });
      });
    });

    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const cat = (this.#shadow.getElementById('cat') as HTMLSelectElement).value as AssetCategory;
      const inst = (this.#shadow.getElementById('inst') as HTMLInputElement).value.trim();
      const id4 = (this.#shadow.getElementById('id4') as HTMLInputElement).value.trim().slice(-4);
      const val = Number((this.#shadow.getElementById('val') as HTMLInputElement).value);
      const cust = (this.#shadow.getElementById('cust') as HTMLInputElement).value.trim();
      const nomsRaw = (this.#shadow.getElementById('noms') as HTMLInputElement).value.trim();
      if (!inst || !id4 || !Number.isFinite(val)) return;
      void store.mutate((d) => {
        const matched: NomineeId[] = [];
        if (nomsRaw) {
          const wanted = nomsRaw.split(/\s*,\s*/).map((n) => n.toLowerCase());
          for (const n of d.nominees) {
            if (wanted.some((w) => n.display_name.toLowerCase().includes(w))) matched.push(n.id);
          }
        }
        const a: Asset = {
          id: newId<AssetId>(),
          category: cat,
          institution: inst,
          identifier_last4: id4,
          estimated_value_paise: toPaise(val),
          custodian_notes: cust,
          nominee_ids: matched,
          document_ids: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        d.assets.push(a);
      });
      form.reset();
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }
}

customElements.define('wasiyat-asset-vault', WasiyatAssetVault);
